<template>
  <div class="card mb-25 border-0 rounded-0 bg-white add-user-card">
    <div class="billetage-form container p-15 p-md-40">
      <h5 class="fw-bold text-black mb-15 mb-md-25">{{ title }}</h5>

      <Form
        ref="billetageForm"
        @submit="addOperation"
        :validation-schema="operationSchema"
        v-slot="{ setFieldValue }"
      >
        <div class="div row">
          <div class="div col-md-12">
            <div class="div row">
              <!-- Sélection de caisse (toujours affichée) -->
              <div class="div col-md-6">
                <div class="div form-group mb-12">
                  <label class="label d-block text-black fw-semibold mb-5">
                    Caisse<span class="span text-danger">*</span>
                  </label>
                  <Field name="caisse" v-slot="{ errors }">
                    <Multiselect v-model="selectedCaisse" :options="caissesOptions" :searchable="true" track-by="value"
                      label="label" placeholder="Sélectionner la caisse" class="w-100"
                      :disabled="caissesOptions.length === 0" @update:modelValue="(value) => {
                        selectedCaisse = value;
                        setFieldValue('caisse', value);
                      }" />
                    <div class="text-danger">{{ errors[0] }}</div>
                  </Field>
                  <ErrorMessage name="caisse" class="text-danger" />

                  <!-- Messages contextuels -->
                  <small v-if="caissesOptions.length === 1" class="text-muted">
                    Cette caisse a été automatiquement sélectionnée
                  </small>
                  <small v-else-if="caissesOptions.length > 1" class="text-info">
                    Vous avez {{ caissesOptions.length }} caisses ouvertes. Veuillez en sélectionner une.
                  </small>
                  <small v-else class="text-danger">
                    Aucune caisse ouverte. Veuillez ouvrir une caisse.
                  </small>
                </div>
              </div>

              <div class="div col-md-6">
                <div class="div form-group mb-12">
                  <div class="div d-flex align-items-center justify-content-between">
                    <label class="label d-block text-black fw-semibold mb-5">
                      Client<span class="span text-danger">*</span>
                    </label>
                    <button class="button btn btn-primary btn-sm" @click.prevent="openAddClientModal">
                      Ajouter
                    </button>
                  </div>
                  <Field name="client" v-slot="{ field, errors }">
                    <Multiselect :options="clientsOptions" :searchable="true" track-by="label" label="label"
                      :model-value="field.value" placeholder="Sélectionner le client" class="w-100"
                      @update:modelValue="field.onChange" @blur="field.onBlur" />
                    <div class="text-danger">{{ errors[0] }}</div>
                  </Field>
                  <ErrorMessage name="client" class="text-danger" />
                </div>
              </div>
            </div>

            <div class="div row">
              <div class="div col-md-6">
                <div class="div form-group mb-12">
                  <label class="label d-block text-black fw-semibold mb-5">
                    Référence document
                  </label>
                  <Field name="referenceDoc" type="text" class="form-control shadow-none fs-md-15 text-black"
                    placeholder="Entrer la référence du document" v-model="referenceDoc" />
                  <ErrorMessage name="referenceDoc" class="text-danger" />
                </div>
              </div>

              <div class="div col-md-6">
                <div class="div form-group mb-12">
                  <label class="label d-block text-black fw-semibold mb-5">
                    Motifs<span class="span text-danger">*</span>
                  </label>
                  <Field name="motif" v-slot="{ field }">
                    <Multiselect v-model="motif" :options="motifOptions" :preserve-search="true" :searchable="true"
                      placeholder="Sélectionner le motif" label="label" track-by="value" v-bind="field" />
                  </Field>
                  <ErrorMessage name="motif" class="text-danger" />
                </div>
              </div>
            </div>

            <div class="div row">
              <div class="div col-md-6">
                <div class="div form-group mb-12">
                  <label class="label d-block text-black fw-semibold mb-5">
                    Montant de l'opération<span class="span text-danger">*</span>
                  </label>
                  <Field name="montant" type="number" class="form-control shadow-none fs-md-15 text-black"
                    placeholder="Entrer le montant de l'opération" v-model="montant" />
                  <ErrorMessage name="montant" class="text-danger" />
                </div>
              </div>
            </div>

            <div class="alert alert-danger" role="alert" v-if="montantError">
              {{ montantError }}
            </div>

            <div class="div row">
              <div class="div col-md-6 mb-md-12">
                <div class="div border border-primary mb-5">
                  <div class="div row fw-bold py-2" style="background-color: #0a59a4">
                    <div class="div col-md-7">
                      <h3 class="h3 fs-4 text-white">Billetage Encaissement</h3>
                    </div>
                    <div class="col-md-5">
                      <h3 class="fs-5 text-white" style="text-align: end;font-weight: bold;"
                        v-if="montantTotalEncaissement">
                        {{ (montantTotalEncaissement) }} Fcfa
                      </h3>
                    </div>
                  </div>
                  <div class="div px-3">
                    <div class="div row mt-2">
                      <div class="div col-md-4"><label class="label fw-semibold">Monnaie</label></div>
                      <div class="div col-md-4"><label class="label fw-semibold">Quantité</label></div>
                      <div class="div col-md-4"><label class="label fw-semibold">Montant</label></div>
                    </div>
                    <hr class="hr mt-0" />
                    <template v-for="(billetage, index) in billetageList" :key="index">
                      <div class="div container-tight m-0 py-1">
                        <div class="div row align-items-center">
                          <div class="div col-md-4 px-1">
                            <input name="monnaie" v-model="billetage.libelle" readonly
                              class="form-control compact-input" placeholder="Monnaie" />
                          </div>
                          <div class="div col-md-4 px-1">
                            <Field :name="`billetageList[${index}].qteBillet`" type="number" min="0"
                              v-model="billetage.qteBillet"
                              @input="event => handleBilletageInput(event, billetage, 'encaissement')"
                              @keypress="restrictInput($event)" class="form-control compact-input" placeholder="Qté" />
                            <ErrorMessage :name="`billetageList[${index}].qteBillet`"
                              class="text-danger compact-error" />
                          </div>
                          <div class="div col-md-4 px-1">
                            <input type="text" v-model="billetage.montant" readonly placeholder="Montant"
                              class="form-control compact-input" />
                          </div>
                        </div>
                      </div>
                    </template>
                  </div>
                </div>
              </div>

              <div class="div col-md-6 mb-md-12">
                <div class="div border border-primary mb-5">
                  <div class="div row fw-bold py-2" style="background-color: #a40a0a">
                    <div class="div col-md-7">
                      <h3 class="h3 fs-4 text-white">Reliquat</h3>
                    </div>
                    <div class="div col-md-5">
                      <h3 class="fs-5 text-white" style="text-align: end;font-weight: bold;">
                        {{ Number(montantTotalSortie) }} Fcfa
                      </h3>
                    </div>
                  </div>
                  <div class="div px-3">
                    <div class="div row mt-2">
                      <div class="div col-md-4"><label class="label fw-semibold">Monnaie</label></div>
                      <div class="div col-md-4"><label class="label fw-semibold">Quantité</label></div>
                      <div class="div col-md-4"><label class="label fw-semibold">Montant</label></div>
                    </div>
                    <hr class="hr mt-0" />
                    <template v-for="(billetage, index) in billetageSortieList" :key="index">
                      <div class="div container-tight m-0 py-1">
                        <div class="div row align-items-center">
                          <div class="div col-md-4 px-1">
                            <input name="monnaie" v-model="billetage.libelle" readonly
                              class="form-control compact-input" placeholder="Monnaie" />
                          </div>
                          <div class="div col-md-4 px-1">
                            <Field :name="`billetageSortieList[${index}].qteBillet`" type="number" min="0"
                              v-model="billetage.qteBillet"
                              @input="event => handleBilletageInput(event, billetage, 'sortie')"
                              @keypress="restrictInput($event)" class="form-control compact-input" placeholder="Qté" />
                            <ErrorMessage :name="`billetageSortieList[${index}].qteBillet`"
                              class="text-danger compact-error" />
                          </div>
                          <div class="div col-md-4 px-1">
                            <input type="text" v-model="billetage.montant" readonly placeholder="Montant"
                              class="form-control compact-input" />
                          </div>
                        </div>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>

            <div class="row mt-3">
              <div class="col-md-10"></div>
              <div class="col-md-2">
                <div class="form-group mb-2">
                  <label class="form-label fw-semibold mb-1 text-start d-block">
                    Reliquat à retourner
                  </label>
                  <input type="number" class="form-control form-control-sm shadow-none text-start"
                    v-model.number="reliquatRetourner" readonly />
                </div>
              </div>
            </div>
            
            <div class="div mb-3 mt-1">
              <button type="submit" class="btn btn-primary top-end" :disabled="!!montantError">
                Envoyer
              </button>
              <button type="button" class="btn btn-danger mx-1" @click="cancelOperation">
                Annuler
              </button>
            </div>
          </div>
        </div>
        </Form>
    </div>

    <!-- Modal Ajout Client -->
    <div class="div modal fade" id="addClientModal" tabindex="-1" aria-labelledby="addClientModalLabel"
      aria-hidden="true">
      <div class="div modal-dialog">
        <div class="div modal-content">
          <div class="div modal-header">
            <h5 class="h5 modal-title" id="addClientModalLabel">Ajouter un client</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="div modal-body">
            <Form ref="clientForm" @submit="addClient" :validation-schema="clientSchema">
              <div class="div mb-3">
                <label for="clientNom" class="form-label">Nom<span class="span text-danger">*</span></label>
                <Field name="nom" type="text" class="form-control" id="clientNom" v-model="newClient.nom"
                  placeholder="Entrer le nom du client" />
                <ErrorMessage name="nom" class="text-danger" />
              </div>
              <div class="div mb-3">
                <label for="prenom" class="form-label">Prénom</label>
                <Field name="prenom" type="text" class="form-control" id="prenom" v-model="newClient.prenom"
                  placeholder="Entrer le prénom du client" />
                <ErrorMessage name="prenom" class="text-danger" />
              </div>
              <div class="div mb-3">
                <label for="telephone" class="form-label">Numéro de téléphone</label>
                <!-- <Field name="telephone" type="text" class="form-control" id="telephone" v-model="newClient.telephone"
                  placeholder="Entrer le numéro de téléphone" />
                <ErrorMessage name="telephone" class="text-danger" /> -->
                <Field name="telephone" v-model="newClient.telephone"  v-slot="{ field }">
                  <vue-tel-input
                    placeholder="Entrer le numéro de téléphone"
                    v-model = "field.value"
                    v-bind = "field"
                    defaultCountry="BJ"
                    mode="international"
                    @validate="validate"
                    class ="shadow-none fs-md-15 text-black py-2">
                  </vue-tel-input>
                </Field>
                <div v-if="!validPhone"  class="text-danger">Veuillez entrer un numéro correcte</div>
                <ErrorMessage name="telephone" class="text-danger"/>
              </div>
              <button type="submit" class="btn btn-primary">Enregistrer</button>
              <button type="button" class="btn btn-secondary ms-2" data-bs-dismiss="modal">Annuler</button>
            </Form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, watch, onMounted, computed } from 'vue';
import { Form, Field, ErrorMessage, useForm } from 'vee-validate';
import * as Yup from 'yup';
import Multiselect from '@vueform/multiselect';
import { Client } from '@/models/Client';
import { Modal } from 'bootstrap';
import { useRouter } from 'vue-router';
import JwtService from '@/services/JwtService';
import { VueTelInput } from 'vue3-tel-input'
import ApiService from '@/services/ApiService';
import { error, monSwal, success } from '@/utils/utils';
import { Motif } from '@/models/Motif';


interface BilletageForm {
  libelle: string;
  qteBillet: number;
  montant: number;
  monnaie: number;
  valueAct: number;
}

interface SelectOption {
  label: string;
  value: number;
}

interface NewClient {
  nom: string;
  prenom: string;
  telephone: string;
}

export default defineComponent({
  name: "AddEncaissement",
  components: {
    Form,
    Field,
    ErrorMessage,
    Multiselect,
    VueTelInput
  },
  // props: {
  //   title: {
  //     type: String,
  //     required: true,
  //   },
  // },
  setup(props) {

    const operationSchema = Yup.object({
      client: Yup.number().required('Le client est obligatoire').nullable(),
      motif: Yup.number().required('Le motif est obligatoire').nullable(),
      montant: Yup.number()
        .typeError('Le montant doit être un nombre valide')
        .required('Le montant est obligatoire')
        .min(0, 'Le montant doit être positif'),
      referenceDoc: Yup.string().trim().nullable(),
      caisse: Yup.number()
        .nullable()
        .required('Veuillez sélectionner une caisse')
        .typeError('Veuillez sélectionner une caisse valide'),
      billetageList: Yup.array().of(
        Yup.object().shape({
          qteBillet: Yup.number()
            .typeError('La quantité doit être un nombre')
            .required('La quantité est obligatoire')
            .integer('La quantité doit être un entier')
            .min(0, 'La quantité ne peut pas être négative'),
        })
      ),
      billetageSortieList: Yup.array().of(
        Yup.object().shape({
          qteBillet: Yup.number()
            .typeError('La quantité doit être un nombre')
            .required('La quantité est obligatoire')
            .integer('La quantité doit être un entier')
            .min(0, 'La quantité ne peut pas être négative'),
        })
      ),
    });

    const clientSchema = Yup.object({
      nom: Yup.string()
        .trim()
        .required('Le nom est obligatoire')
        .min(1, 'Le nom ne peut pas être vide'),
      prenom: Yup.string().trim().nullable(),
      telephone: Yup.string().trim().nullable(),
    });



    const router = useRouter();
    const billetageList = reactive<BilletageForm[]>([]);
    const billetageSortieList = reactive<BilletageForm[]>([]);
    const monnaieList = ref<any[]>([]);
    const montantTotalEncaissement = ref<number | null>(null);
    const montantTotalSortie = ref<number | null>(null);
    const clientsOptions = ref<SelectOption[]>([]);
    const clients = ref<Client[]>([]);
    const client = ref<number | null>(null);
    const montant = ref<number | null>(null);
    const motif = ref<number | null>(null);
    const motifOptions = ref<SelectOption[]>([]);
    const referenceDoc = ref<string>('');
    const newClient = reactive<NewClient>({ nom: '', prenom: '', telephone: '' });
    const typeOperationId = ref<number>(1);
    const billetageForm = ref<InstanceType<typeof Form> | null>(null);
    const totalEntree = ref<number>(0);
    const totalSorties = ref<number>(0);
    // const { resetForm, setFieldValue } = useForm();
    const reliquatRetourner = ref(0);
    const caissesOptions = ref<SelectOption[]>([]);
    const selectedCaisse = ref<number | null>(null);
       const  codepay= ref();
    const  nationalnumlber= ref();
    const telephone=ref();
    const validPhone=ref<boolean>(false);
    const numberPhone=ref();
    const  numeroNational = ref();
    const defaultCountriy = 'Bénin';

      function onInput(phone, phoneObject, input) {
      //
    }


     function validate(phoneObject) {
      validPhone.value = phoneObject.valid;
      if (phoneObject.valid == true) {
        telephone.value = phoneObject.number;
        codepay.value= phoneObject.countryCallingCode;
        nationalnumlber.value = phoneObject.nationalNumber;
        numberPhone.value = phoneObject.nationalNumber;

      }else{
        telephone.value="";
        codepay.value= "";
        nationalnumlber.value= "";
        numberPhone.value="";
      }
    }


    const montantError = computed(() => {
      if (
        montant.value === null ||
        montantTotalEncaissement.value === null
      ) {
        return null;
      }

      const encaissement = montantTotalEncaissement.value;
      const sortie = montantTotalSortie.value || 0;
      const montantAttendu = montant.value;

      if (encaissement < montantAttendu) {
        return `Le montant encaissé (${encaissement} Fcfa) est inférieur au montant attendu (${montantAttendu} Fcfa).`;
      }

      if (encaissement < montantAttendu + sortie) {
        return `Le reliquat saisi (${sortie} Fcfa) dépasse le montant disponible.`;
      }

      if (encaissement > montantAttendu && sortie === 0) {
        return `Un reliquat de ${encaissement - montantAttendu} Fcfa doit être retourné.`;
      }

      return null;
    });


    const getAllMonnaies = async () => {
      try {
        const res = await ApiService.get("/all/monnaies");
        console.log("resss", res);
        monnaieList.value = res.data.data.data;
        billetageList.splice(0, billetageList.length);
        billetageSortieList.splice(0, billetageSortieList.length);
        monnaieList.value.forEach((element) => {
          const billetage = {
            libelle: element.libelle,
            montant: 0,
            valueAct: element.valeur,
            monnaie: element.id,
            qteBillet: 0,
          };
          billetageList.push({ ...billetage });
          billetageSortieList.push({ ...billetage });
        });
      } catch (err) {
        console.error("Erreur lors de la récupération des monnaies:", err);
        error("Erreur lors de la récupération des monnaies");
      }
    };

    const fetchClients = async () => {
      try {
        const { data } = await ApiService.get("/all/clients");
        const donnees = data.data.data;
        clientsOptions.value = donnees.map((cl: Client) => {
          let label = cl.nomClient || '';
          if (cl.prenomClient) {
            label += ` ${cl.prenomClient}`;
          }
          if (cl.telephone1) {
            label += ` - ${cl.telephone1}`;
          }
          return {
            label: label.trim(),
            value: cl.id,
          };
        });
        clients.value = donnees;
      } catch (err) {
        console.error("Erreur lors de la récupération des clients:", err);
        error("Erreur lors de la récupération des clients");
      }
    };

    const fetchCaissesOuvertes = async () => {
      try {
        console.log("→ Début récupération caisses ouvertes");
        const response = await ApiService.get("/caisses/toutes-ouvertes");
        console.log("Réponse HTTP complète :", response);
        console.log("Status :", response.status);
        console.log("Headers :", response.headers);

        const data = response.data;
        console.log("Structure data brute :", data);

        // Essaie différentes extractions possibles (selon la vraie structure)
        let caisses: Record<string, any>[] = [];
        if (data && Array.isArray(data)) {
          caisses = data;
        } else if (data?.data && Array.isArray(data.data)) {
          caisses = data.data;
        } else if (data?.data?.data && Array.isArray(data.data.data)) {
          caisses = data.data.data;
        } else if (data?.caisses && Array.isArray(data.caisses)) {
          caisses = data.caisses;
        }

        console.log("Caisses extraites (longueur) :", caisses.length);
        console.log("Caisses extraites (contenu) :", caisses);

        if (caisses.length === 0) {
          error("Aucune caisse n'est ouverte. Veuillez ouvrir une caisse avant de faire un encaissement.");
          router.push({ name: 'ListeCaisse' });
          return;
        }

        caissesOptions.value = caisses.map((c: any) => ({
          label: c.nom || c.name || `Caisse #${c.id}`,
          value: c.id,
        }));

        console.log("Options finales pour Multiselect :", caissesOptions.value);

        if (caissesOptions.value.length === 1) {
          selectedCaisse.value = caissesOptions.value[0].value;
          if (billetageForm.value) {
            billetageForm.value.setFieldValue('caisse', caissesOptions.value[0].value);
          }
          console.log("Caisse unique → auto-sélection :", selectedCaisse.value);
        } else {
          selectedCaisse.value = null;
          if (billetageForm.value) {
            billetageForm.value.setFieldValue('caisse', null);
          }
          console.log("Plusieurs caisses attente sélection manuelle");
        }
      } catch (err: any) {
        console.error("Erreur fetch caisses :", err);
        console.error("Response erreur (si disponible) :", err.response);
        const errorMessage = err.response?.data?.message || "Erreur lors de la récupération des caisses ouvertes";
        error(errorMessage);

        if (err.response?.status === 401 || err.response?.status === 403) {
          console.warn("Problème d'auth redirection possible");
        }
        if (err.response?.status === 404) {
          router.push({ name: 'ListeCaisses' });
        }
      }
    };

    watch(selectedCaisse, (newValue) => {
      if (newValue && billetageForm.value) {
        billetageForm.value.setFieldValue('caisse', newValue);
      }
    });
    const fetchMotifs = async (typeOperationId: number) => {
      try {
        const { data } = await ApiService.query(`/motifs/by-type`, {
          params: { typeOperationId },
        });

        console.log("donnnneees", data);

        const donnees = data.data.data;
        motifOptions.value = donnees.map((mo: Motif) => ({
          label: `${mo.nom}`,
          value: mo.id,
        }));
      } catch (err) {
        console.error("Erreur lors de la récupération des motifs:", err);
        error("Erreur lors de la récupération des motifs");
      }
    };

    const handleBilletageInput = (event: Event, billetage: BilletageForm, type: 'encaissement' | 'sortie') => {
      const newValue = Number((event.target as HTMLInputElement).value);
      billetage.qteBillet = isNaN(newValue) || newValue < 0 ? 0 : newValue;
      updateMontant(billetage, type);
    };

    const updateMontant = (billetage: BilletageForm, type: 'encaissement' | 'sortie') => {
      billetage.montant = billetage.qteBillet * billetage.valueAct || 0;
      if (type === 'encaissement') {
        calculateTotalEncaissement();
      } else {
        calculateTotalSortie();
      }
    };

    const calculateReliquatRetourner = () => {
      const encaissement = montantTotalEncaissement.value || 0;
      const sortie = montantTotalSortie.value || 0;
      const montantAttendu = montant.value || 0;
      reliquatRetourner.value = encaissement - (montantAttendu + sortie);
    };

    watch([montantTotalEncaissement, montant, montantTotalSortie], () => {
      calculateReliquatRetourner();
    });

    const calculateTotalEncaissement = () => {
      const total = billetageList.reduce((total, billetage) => {
        return total + (billetage.montant || 0);
      }, 0);
      montantTotalEncaissement.value = total || null;
      calculateReliquatRetourner();
    };

    const calculateTotalSortie = () => {
      const total = billetageSortieList.reduce((total, billetage) => {
        return total + (billetage.montant || 0);
      }, 0);
      montantTotalSortie.value = total || null;
      calculateReliquatRetourner();
    };

    const openAddClientModal = () => {
      newClient.nom = '';
      newClient.prenom = '';
      newClient.telephone = '';
      const modalElement = document.getElementById('addClientModal');
      if (modalElement) {
        const modal = new Modal(modalElement);
        modal.show();
      }
    };

    const addClient = async (values: any, { resetForm }: any) => {
      try {
        const payload = {
          nom: values.nom,
          prenom: values.prenom || null,
          telephone: values.telephone || null,
          typeClient: 'physique',
        };
        const { data } = await ApiService.post("/clients", payload);
        const newClientData = data.data;

        let label = newClientData.nom || '';
        if (newClientData.prenom) {
          label += ` ${newClientData.prenom}`;
        }
        if (newClientData.telephone) {
          label += ` - ${newClientData.telephone}`;
        }

        clientsOptions.value.push({
          label: label.trim(),
          value: newClientData.id,
        });
        clients.value.push(newClientData);
        client.value = newClientData.id;
        success("Client ajouté avec succès");
        resetForm();
        const modalElement = document.getElementById('addClientModal');
        if (modalElement) {
          const modal = Modal.getInstance(modalElement);
          modal?.hide();
        }
      } catch (err: any) {
        console.error("Erreur lors de l'ajout du client:", err);
        const errorMessage = err.response?.data?.message || "Erreur lors de l'ajout du client";
        error(errorMessage);
      }
    };

    const cancelOperation = async () => {
      const confirmed = await monSwal(
        'Voulez-vous vraiment annuler lopération ?',
        'Confirmer',
        'btn btn-primary',
        'Annuler',
        'Lopération a été annulée !'
      );
      if (confirmed) {
        if (billetageForm.value) {
          billetageForm.value.resetForm();
        } else {
          console.warn('billetageForm nest pas défini');
        }

        billetageList.length = 0;
        billetageSortieList.length = 0;
        montant.value = null;
        motif.value = null;
        client.value = null;
        referenceDoc.value = '';
        selectedCaisse.value = null;
        await getAllMonnaies();
        router.push('/ajouter-encaissement');
      }
    };

    const addOperation = async (values: any, { resetForm }: any) => {
      if (montantError.value) {
        error(montantError.value);
        return;
      }

      if (!selectedCaisse.value) {
        error("Veuillez sélectionner une caisse");
        return;
      }

      try {
        const payload = {
          clientId: values.client,
          motifId: values.motif,
          referenceDoc: values.referenceDoc || null,
          montant: values.montant,
          typeOperationId: typeOperationId.value,
          caisseId: selectedCaisse.value,
          billetagesEncaissement: billetageList,
          montantTotalEncaissement: montantTotalEncaissement.value,
          billetagesSortie: billetageSortieList,
          montantTotalSortie: montantTotalSortie.value || 0,
          reliquatRetourner: reliquatRetourner.value,
          date: new Date(),
          type: 'Encaissement',
        };

        console.log("Payload envoyé:", payload);

        await ApiService.post("encaissements/operations", payload);
        success(`Opération d'encaissement de ${montantTotalEncaissement.value} enregistrée avec succès !`);
        resetForm();
        billetageList.length = 0;
        billetageSortieList.length = 0;
        montant.value = null;
        motif.value = null;
        client.value = null;
        referenceDoc.value = '';
        selectedCaisse.value = null;
        await getAllMonnaies();
        router.push({ name: 'ListeOperationPage' });
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || "Erreur lors de l'ajout de l'opération";
        console.error("Erreur serveur:", errorMessage);
        error(errorMessage);
      }
    };

    watch(billetageList, () => {
      calculateTotalEncaissement();
    }, { deep: true });

    watch(billetageSortieList, () => {
      calculateTotalSortie();
      calculateReliquatRetourner();
    }, { deep: true });

    onMounted(() => {
      getAllMonnaies();
      fetchClients();
      fetchMotifs(typeOperationId.value);
      fetchCaissesOuvertes();
    });

    const restrictInput = (event: KeyboardEvent) => {
      if (!/^[0-9]*$/.test(event.key) && event.key !== 'Backspace') {
        event.preventDefault();
      }
    };

    return {
      reliquatRetourner,
      billetageList,
      billetageSortieList,
      montantTotalEncaissement,
      montantTotalSortie,
      operationSchema,
      clientSchema,
      restrictInput,
      handleBilletageInput,
      addOperation,
      addClient,
      clientsOptions,
      client,
      montant,
      motif,
      motifOptions,
      referenceDoc,
      montantError,
      newClient,
      openAddClientModal,
      cancelOperation,
      billetageForm,
      caissesOptions,
      selectedCaisse,
      validPhone,
      defaultCountriy,
      numeroNational,
      telephone,validate
    };
  },
});
</script>

<style scoped>
.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.montant-total {
  text-align: left;
  font-weight: bold;
  font-size: 1rem;
  margin-top: 8px;
}

.text-danger {
  font-size: 0.75rem;
  color: #dc3545;
}

.compact-input {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  height: 30px;
}

.compact-error {
  font-size: 0.75rem;
  margin-top: 2px;
}

.container-tight {
  margin: 0;
  padding: 2px 0;
}
</style>